import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Button,
	DealStageElement,
	Dialog, DialogContent, DialogTitle,
	PanelCardHeader,
	PanelFilters,
	PanelFooter,
	Stages,
} from '@rentflowdev/ui'
import { useGetCompanyProfileQuery, useGetContactKycQuery } from '@/api'
import { CURRENCY_SIGNS } from '@/types'
import type { CompanyProfile, ContactKyc, Deal, DocumentTemplate } from '@/types'
import { useWizardDraftsStore } from '@/lib/wizard-drafts-store'
import { StepBasics }   from './step-basics'
import { StepSubject }  from './step-subject'
import { StepLandlord } from './step-landlord'
import { StepTenant }   from './step-tenant'
import { StepTerms }    from './step-terms'
import { StepPayments } from './step-payments'
import {
	WIZARD_STEP_ORDER,
	type BasicsValues,
	type CreateDocumentPayload,
	type LandlordValues,
	type PaymentsUtilityRow,
	type PaymentsValues,
	type SubjectValues,
	type TenantValues,
	type TermsValues,
	type WizardState,
	type WizardStepKey,
} from './types'

/**
 * Read-only overrides for the wizard sourced from a real Property record.
 * Callers pass these so the wizard pre-fills accurate values from the property
 * instead of the denormalized deal snapshot.
 */
export interface WizardPropertyOverrides {
	objectType?:      string
	address?:         string
	cadastralNumber?: string
	area?:            string
	rentAmount?:      string
	rentCurrency?:    string
	depositAmount?:   string
	depositCurrency?: string
	penaltyRate?:     string
	/** Снимок НДС из условий аренды — ставка и готовая формулировка. */
	vatPercent?:      string
	vatIncluded?:     boolean
	vatClause?:       string
}

interface CreateDocumentWizardProps {
	open: boolean
	template: DocumentTemplate | null
	deal: Deal
	property?: WizardPropertyOverrides
	onOpenChange: (open: boolean) => void
	/**
	 * Called on final "Создать". `payload` mirrors the expected backend shape
	 * (see types.ts `CreateDocumentPayload`); once #63 ships the backend API,
	 * wire it straight to the mutation. `template` + `title` are kept for the
	 * current in-memory seed flow in `DealDocumentsSection`.
	 */
	onSave: (payload: {
		template: DocumentTemplate
		title:    string
		data:     CreateDocumentPayload
	}) => Promise<void>
	existingNumbers?: string[]
}

const STEP_FORM_ID = 'create-document-wizard-step'

/**
 * Old stub IDs that lived in the wizard before we wired CompanyProfile. Drafts
 * persisted in localStorage may still hold these; we treat them as empty so
 * the freshly-built defaults win.
 */
const STALE_LANDLORD_IDS = new Set(['bank-acc-1', 'sig-1', 'profile-bank', 'profile-sig'])

function isFresh(value: string | undefined): boolean {
	return Boolean(value) && !STALE_LANDLORD_IDS.has(value as string)
}

function formatBankAccountLabel(profile: { bank?: string; bankAccount?: string }): string {
	const tail  = profile.bankAccount?.slice(-4)
	const tail4 = tail ? `…${tail}` : ''
	const parts = [tail4, profile.bank].filter(Boolean)
	if (parts.length > 0) return parts.join(' · ')
	return profile.bankAccount ?? ''
}

/**
 * Merge missing/empty auto-populated fields from defaults into a loaded draft.
 * Drafts persist in localStorage across deploys, so when we widen defaults (or
 * `CompanyProfile` finally loads from the API after a draft was created with no
 * profile) we need to backfill empty fields without nuking unrelated user edits.
 */
function applyDefaults(state: WizardState, defaults: WizardState): WizardState {
	return {
		...state,
		landlord: {
			...state.landlord,
			bankAccountId: isFresh(state.landlord.bankAccountId) ? state.landlord.bankAccountId : defaults.landlord.bankAccountId,
			signatoryId:   isFresh(state.landlord.signatoryId)   ? state.landlord.signatoryId   : defaults.landlord.signatoryId,
			fullName:      state.landlord.fullName      || defaults.landlord.fullName,
			inn:           state.landlord.inn           || defaults.landlord.inn,
			organization:  state.landlord.organization  || defaults.landlord.organization,
			ogrn:          state.landlord.ogrn          || defaults.landlord.ogrn,
			legalAddress:  state.landlord.legalAddress  || defaults.landlord.legalAddress,
		},
		tenant: {
			...state.tenant,
			byProxy:             state.tenant.byProxy || defaults.tenant.byProxy,
			registrationAddress: state.tenant.registrationAddress || defaults.tenant.registrationAddress,
			passportSeries:      state.tenant.passportSeries      || defaults.tenant.passportSeries,
			passportNumber:      state.tenant.passportNumber      || defaults.tenant.passportNumber,
			passportIssuedBy:    state.tenant.passportIssuedBy    || defaults.tenant.passportIssuedBy,
			// Added to the schema after drafts already existed in localStorage:
			// a pre-deploy draft hydrates them as undefined, and z.string()
			// would block the tenant step with no visible reason.
			inn:                 state.tenant.inn                 || defaults.tenant.inn,
			ogrn:                state.tenant.ogrn                || defaults.tenant.ogrn,
			kpp:                 state.tenant.kpp                 || defaults.tenant.kpp,
			bank:                state.tenant.bank                || defaults.tenant.bank,
			bankAccount:         state.tenant.bankAccount         || defaults.tenant.bankAccount,
			corrAccount:         state.tenant.corrAccount         || defaults.tenant.corrAccount,
			bic:                 state.tenant.bic                 || defaults.tenant.bic,
			signatoryId:         state.tenant.signatoryId         || defaults.tenant.signatoryId,
			baseDocumentNumber:  state.tenant.baseDocumentNumber  || defaults.tenant.baseDocumentNumber,
			validFromIso:        state.tenant.validFromIso        || defaults.tenant.validFromIso,
			validToIso:          state.tenant.validToIso          || defaults.tenant.validToIso,
		},
	}
}

export function kycToTenantType(tenantType: ContactKyc['tenantType']): import('./types').TenantType {
	if (tenantType === 'ENTREPRENEUR') return 'entrepreneur'
	if (tenantType === 'COMPANY' || tenantType === 'ENTITY') return 'company'
	return 'individual'
}

function parseNum(raw: string | undefined, fallback: number): number {
	if (!raw) return fallback
	const n = parseFloat(raw)
	return Number.isFinite(n) && n > 0 ? n : fallback
}

// ── Display string builders ───────────────────────────────────────────────────

function pluralize(n: number, unit: string): string {
	const abs = Math.abs(n) % 100
	const mod = abs % 10
	if (unit === 'months') {
		if (abs >= 11 && abs <= 14) return `${n} месяцев`
		if (mod === 1) return `${n} месяц`
		if (mod >= 2 && mod <= 4) return `${n} месяца`
		return `${n} месяцев`
	}
	if (unit === 'days') {
		if (abs >= 11 && abs <= 14) return `${n} дней`
		if (mod === 1) return `${n} день`
		if (mod >= 2 && mod <= 4) return `${n} дня`
		return `${n} дней`
	}
	return `${n} ${unit}`
}

function fmtAmount(amount: number, currency: string): string {
	const symbol = CURRENCY_SIGNS[currency] ?? currency
	return `${amount.toLocaleString('ru-RU')} ${symbol}`
}

const RENT_TERM_KIND: Record<string, string> = {
	determined: 'определённый', indeterminate: 'неопределённый',
}
// {{subject.purpose}} is the raw wizard enum; a contract must print the
// Russian phrase, not 'commercial'.
const OBJECT_PURPOSE: Record<string, string> = {
	residence: 'проживание', commercial: 'коммерческая деятельность', mixed: 'смешанное использование',
}
const TRANSFER_MODE: Record<string, string> = {
	act: 'по акту приёма-передачи', noAct: 'без составления акта',
}
const RETURN_MODE: Record<string, string> = {
	act: 'по акту возврата помещения', noAct: 'без составления акта',
}
const TRANSFER_TIMING: Record<string, string> = {
	afterContractEnd: 'после окончания срока договора',
	onAgreedDate:     'в согласованную сторонами дату',
}
const TRANSFER_MOMENT: Record<string, string> = {
	onSigning:       'с момента подписания договора',
	onActualTransfer:'с момента фактической передачи помещения',
}
const RETURN_CONDITION: Record<string, string> = {
	usable:     'в пригодном для использования состоянии',
	normalWear: 'с учётом нормального износа',
	withRepair: 'с проведением ремонта',
	clean:      'в чистоте и порядке',
}
const IMPROVEMENTS_FATE: Record<string, string> = {
	noCompensation:     'без компенсации арендодателем',
	compensated:        'с компенсацией арендодателем',
	bySeparateAgreement:'по отдельному соглашению сторон',
	custom:             '',
}
const PAYMENT_FORM: Record<string, string> = {
	cashless: 'безналичным банковским переводом',
	cash:     'наличными денежными средствами',
}
const PERIOD_START: Record<string, string> = {
	monthStart:    'с первого числа текущего месяца',
	yearStart:     'с первого числа текущего года',
	contractStart: 'с даты начала договора',
}
const PAYMENT_DAY: Record<string, string> = {
	every5th:  '5-го числа', every10th: '10-го числа',
	every15th: '15-го числа', every20th: '20-го числа',
}
const DEPOSIT_TIMING: Record<string, string> = {
	afterContractEnd: 'после окончания срока договора',
	onAgreedDate:     'в согласованную сторонами дату',
}
const DEPOSIT_RETURN: Record<string, string> = {
	lastPeriodOffset: 'засчитывается в счёт оплаты последнего периода аренды',
	refund:           'возвращается арендатору в полном объёме',
}

function fmtDate(iso: string | undefined): string {
	if (!iso) return ''
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return iso
	return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function buildDisplay(state: WizardState): import('./types').DocumentDisplay {
	const { basics, subject, terms, payments, tenant } = state
	return {
		basics: {
			date: fmtDate(basics.dateIso),
		},
		subject: {
			purpose: OBJECT_PURPOSE[subject.purpose] ?? subject.purpose,
		},
		terms: {
			rentTermKind:      RENT_TERM_KIND[terms.rentTermKind]      ?? terms.rentTermKind,
			contractFrom:      fmtDate(terms.contractFromIso),
			contractTo:        fmtDate(terms.contractToIso),
			effectiveFrom:     fmtDate(terms.effectiveFromIso),
			landlordNotice:    pluralize(terms.landlordNoticeCount, terms.landlordNoticeUnit),
			tenantNotice:      pluralize(terms.tenantNoticeCount,   terms.tenantNoticeUnit),
			transferMode:      TRANSFER_MODE[terms.transferMode]       ?? terms.transferMode,
			transferActPeriod: pluralize(terms.transferActCount, terms.transferActUnit),
			transferActTiming: TRANSFER_TIMING[terms.transferActTiming] ?? terms.transferActTiming,
			transferMoment:    TRANSFER_MOMENT[terms.transferMoment]   ?? terms.transferMoment,
			returnMode:        RETURN_MODE[terms.returnMode]           ?? terms.returnMode,
			returnPeriod:      pluralize(terms.returnCount, terms.returnUnit),
			returnTiming:      TRANSFER_TIMING[terms.returnTiming]     ?? terms.returnTiming,
			returnConditions:  terms.returnConditions
				.map((c) => RETURN_CONDITION[c] ?? c)
				.join(', '),
			improvementsFate:  IMPROVEMENTS_FATE[terms.improvementsFate] ?? terms.improvementsFate,
		},
		payments: {
			rentAmount:        fmtAmount(payments.rentAmount, payments.rentCurrency),
			penaltyPercent:    payments.penaltyPercent.toLocaleString('ru-RU'),
			// Формулировка приходит с сервера вместе со ставкой: «не облагается по
			// ст. 149», «освобождён по ст. 145» и «не плательщик НДС» — разные
			// основания, а прежняя ветка печатала «НДС не облагается» даже когда
			// НДС начисляется сверху.
			vatClause:         payments.rentVatClause
				?? (payments.rentVatRate > 0
					? (payments.rentVatInclusive
						? `в том числе НДС ${payments.rentVatRate}%`
						: `кроме того НДС ${payments.rentVatRate}%`)
					: 'Без НДС'),
			paymentForm:       PAYMENT_FORM[payments.paymentForm]     ?? payments.paymentForm,
			paymentDay:        PAYMENT_DAY[payments.paymentDay]       ?? payments.paymentDay,
			periodStart:       PERIOD_START[payments.periodStart]     ?? payments.periodStart,
			firstPaymentPeriod:pluralize(payments.firstPaymentDays, 'days'),
			depositClause:     payments.depositEnabled
				? fmtAmount(payments.depositAmount, payments.depositCurrency)
				: 'не предусмотрен',
			depositTermPeriod: pluralize(payments.depositTermCount, payments.depositTermUnit),
			depositTiming:     DEPOSIT_TIMING[payments.depositTermTiming] ?? payments.depositTermTiming,
			depositReturn:     DEPOSIT_RETURN[payments.depositReturn]  ?? payments.depositReturn,
			penaltyGrace:      pluralize(payments.penaltyGraceCount, payments.penaltyGraceUnit),
		},
		tenant: {
			proxyClause: tenant.byProxy
				? [
					'действующего на основании',
					tenant.baseDocument || 'доверенности',
					tenant.baseDocumentNumber ? `№ ${tenant.baseDocumentNumber}` : '',
					tenant.validFromIso ? `от ${fmtDate(tenant.validFromIso)}` : '',
					tenant.validToIso   ? `по ${fmtDate(tenant.validToIso)}` : '',
				].filter(Boolean).join(' ')
				: '',
		},
	}
}

function buildInitialState(deal: Deal, property?: WizardPropertyOverrides, profile?: CompanyProfile | null, kyc?: ContactKyc | null): WizardState {
	const today = new Date().toISOString().slice(0, 10)
	const rentAmount    = parseNum(property?.rentAmount  ?? deal.amount,         50000)
	const rentCurrency  = property?.rentCurrency ?? deal.currency ?? 'RUB'
	const depositAmount = parseNum(property?.depositAmount,                       rentAmount)
	const penaltyRate   = parseNum(property?.penaltyRate,                         0.1)
	return {
		basics: {
			number:       '',
			dateIso:      today,
			documentType: 'LEASE_CONTRACT',
		},
		subject: {
			propertyId:      deal.propertyId ?? '',
			objectType:      property?.objectType      ?? deal.propertyTitle   ?? '',
			address:         property?.address         ?? deal.propertyAddress ?? '',
			cadastralNumber: property?.cadastralNumber ?? '',
			area:            property?.area            ?? '',
			areaUnit:        'м²',
			purpose:         'residence',
			services:        [],
		},
		landlord: {
			// Auto-populated from the current user's CompanyProfile (org settings).
			// `bankAccountId` / `signatoryId` are misnamed for now — until backend
			// ships dedicated `AccountBankAccount[]` / `AccountSignatory[]` lists
			// they hold freeform display text (formatted bank label / signatory
			// full name), not real FKs. UI renders them as plain Inputs so the
			// user can type one-off overrides even when the profile is empty.
			bankAccountId: formatBankAccountLabel(profile ?? {}),
			signatoryId:   profile?.signatory ?? profile?.signatories?.[0]?.fullName ?? '',
			fullName:      profile?.signatory ?? profile?.signatories?.[0]?.fullName ?? '',
			inn:           profile?.inn       ?? '',
			organization:  profile?.name      ?? '',
			ogrn:          profile?.ogrn      ?? '',
			legalAddress:  profile?.address   ?? profile?.registrationAddr ?? '',
		},
		tenant: {
			contragentId:        deal.contactId                               ?? '',
			contragentName:      deal.clientName ?? kyc?.fullName             ?? '',
			tenantType:          kyc ? kycToTenantType(kyc.tenantType)        : 'individual',
			registrationAddress: kyc?.registrationAddr                        ?? '',
			phone:               deal.clientPhone ?? kyc?.phone               ?? '',
			passportSeries:      kyc?.passportSeries                          ?? '',
			passportNumber:      kyc?.passportNumber                          ?? '',
			passportIssuedBy:    kyc?.passportIssuedBy                        ?? '',
			inn:                 kyc?.inn                                     ?? '',
			ogrn:                kyc?.ogrn                                    ?? '',
			kpp:                 kyc?.kpp                                     ?? '',
			byProxy:             Boolean(kyc?.representative || kyc?.attorneyNumber),
			signatoryId:         kyc?.representative                          ?? '',
			baseDocument:        '',
			baseDocumentNumber:  kyc?.attorneyNumber                          ?? '',
			validFromIso:        kyc?.attorneyFrom                            ?? '',
			validToIso:          kyc?.attorneyTo                              ?? '',
			bank:                kyc?.bank                                    ?? '',
			bankAccount:         kyc?.bankAccount                             ?? '',
			corrAccount:         kyc?.corrAccount                             ?? '',
			bic:                 kyc?.bik                                     ?? '',
		},
		terms: {
			rentTermKind:          'determined',
			contractFromIso:       deal.startDate ?? today,
			contractToIso:         deal.endDate   ?? '',
			effectiveFromIso:      deal.startDate ?? today,
			effectiveMatchesStart: true,

			landlordNoticeCount:   3,
			landlordNoticeUnit:    'months',
			tenantNoticeCount:     3,
			tenantNoticeUnit:      'months',

			transferMode:                'act',
			transferActCount:            3,
			transferActUnit:             'months',
			transferActTiming:           'afterContractEnd',
			transferMoment:              'onSigning',
			transferExecutorSignatoryId: '',
			transferCustomerSignatoryId: '',

			returnMode:       'act',
			returnCount:      3,
			returnUnit:       'months',
			returnTiming:     'afterContractEnd',
			returnConditions: ['normalWear', 'clean'],

			improvementsFate:    'noCompensation',
			improvementsConsent: '',

			otherConditions: '',

			attachments: [],
		},
		payments: {
			rentAmount:       rentAmount,
			rentCurrency:     rentCurrency,
			// Ставка берётся из условий аренды объекта — она выведена из налогового
			// режима. Прежнее значение 12% не существует в российском НДС и
			// печаталось в договор как есть.
			rentVatRate:      property?.vatPercent != null ? Number(property.vatPercent) : 0,
			rentVatInclusive: property?.vatIncluded ?? true,
			rentVatClause:    property?.vatClause,

			utilities: [],

			depositEnabled:         depositAmount > 0,
			depositAmount:          depositAmount,
			// The deposit has its own stored currency; pinning it to the rent
			// currency mislabels the sum in a read-only field.
			depositCurrency:        property?.depositCurrency ?? rentCurrency,
			depositTermCount:       3,
			depositTermUnit:        'months',
			depositTermTiming:      'afterContractEnd',
			depositReturn:          'lastPeriodOffset',
			depositNoReturnOnEarly: true,

			penaltyEnabled:    true,
			penaltyPercent:    penaltyRate,
			penaltyGraceCount: 3,
			penaltyGraceUnit:  'days',

			firstPaymentDays:  3,
			paymentForm:       'cashless',
			landlordAccountId: '',

			periodStart:     'monthStart',
			paymentDay:      'every5th',
			paymentPriority: 'penaltyOverdueCurrent',
		},
	}
}

function parseArea(raw: string): number {
	const n = parseFloat(raw.replace(',', '.'))
	return Number.isFinite(n) ? n : 0
}

function buildPayload(args: {
	dealId:     string
	templateId: string
	title:      string
	state:      WizardState
}): CreateDocumentPayload {
	const { state } = args
	return {
		dealId:       args.dealId,
		templateId:   args.templateId,
		title:        args.title,
		documentType: state.basics.documentType,
		basics: {
			number:  state.basics.number,
			dateIso: state.basics.dateIso,
		},
		subject: {
			propertyId:      state.subject.propertyId,
			objectType:      state.subject.objectType,
			address:         state.subject.address,
			cadastralNumber: state.subject.cadastralNumber,
			area:            parseArea(state.subject.area),
			purpose:         state.subject.purpose,
			services: state.subject.services.filter((s) => s.selected).map((s) => ({
				name:      s.name,
				unit:      s.unit,
				qty:       s.qty,
				unitPrice: s.unitPrice,
				vatRate:   s.vatRate,
			})),
		},
		landlord: state.landlord,
		tenant:   state.tenant,
		terms:    state.terms,
		payments: state.payments,
		display:  buildDisplay(state),
	}
}

export function CreateDocumentWizard({
	open,
	template,
	deal,
	property,
	onOpenChange,
	onSave,
	existingNumbers = [],
}: CreateDocumentWizardProps) {
	const { t } = useTranslation()

	// Landlord-side defaults come from the current user's CompanyProfile
	// (organisation settings). The query is cached by RTK Query, so calling it
	// here doesn't refetch when the wizard is reopened.
	const { data: profile } = useGetCompanyProfileQuery()
	// Tenant KYC — pre-fills passport, bank, registration address from contact record.
	const { data: kyc } = useGetContactKycQuery(deal.contactId ?? '', { skip: !deal.contactId })

	const draftKey  = template ? `deal:${deal.id}:document:${template.id}` : null
	const getDraft  = useWizardDraftsStore((s) => s.getDraft)
	const setDraft  = useWizardDraftsStore((s) => s.setDraft)
	const clearDraft = useWizardDraftsStore((s) => s.clearDraft)

	const [isSaving, setIsSaving] = useState(false)

	/** -1 = gateway step (basics), 0..N-1 = wizard steps (subject/terms/payments/review) */
	const [stepIdx, setStepIdx] = useState<number>(() => {
		if (!draftKey) return -1
		return getDraft(draftKey)?.stepIdx ?? -1
	})
	const [state, setState] = useState<WizardState>(() => {
		const defaults = buildInitialState(deal, property, profile, kyc)
		if (!draftKey) return defaults
		const draft = getDraft(draftKey)
		const stored = draft?.state as WizardState | undefined
		return stored ? applyDefaults(stored, defaults) : defaults
	})

	// Re-hydrate when template/profile/kyc changes (all come from async RTK Query,
	// so may arrive after initial render).
	useEffect(() => {
		if (!draftKey) return
		const defaults = buildInitialState(deal, property, profile, kyc)
		const draft = getDraft(draftKey)
		const stored = draft?.state as WizardState | undefined
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setState(stored ? applyDefaults(stored, defaults) : defaults)

		setStepIdx(draft?.stepIdx ?? -1)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [draftKey, profile, kyc])

	// Persist on every change while wizard is open.
	useEffect(() => {
		if (!draftKey || !open) return
		setDraft(draftKey, { state, stepIdx })
	}, [draftKey, open, state, stepIdx, setDraft])

	const onGatewayStep  = stepIdx === -1
	const currentWizardStep: WizardStepKey | null = onGatewayStep ? null : WIZARD_STEP_ORDER[stepIdx]
	const isLastStep  = stepIdx === WIZARD_STEP_ORDER.length - 1

	const title = useMemo(
		() => t('deals.documents.wizard.title', {
			number: state.basics.number ? `№${state.basics.number}` : '',
		}).trim(),
		[t, state.basics.number],
	)

	function closeAndReset() {
		if (draftKey) clearDraft(draftKey)
		onOpenChange(false)
		setStepIdx(-1)
		setState(buildInitialState(deal, property, profile, kyc))
	}

	/** Close without wiping the draft — preserves wizard progress for next open. */
	function closePreservingDraft() {
		onOpenChange(false)
	}

	async function advance(nextState: WizardState = state) {
		if (onGatewayStep) {
			setStepIdx(0)
			return
		}
		if (!isLastStep) {
			setStepIdx((i) => i + 1)
			return
		}
		if (!template) return
		const finalTitle = title || template.title
		setIsSaving(true)
		try {
			await onSave({
				template,
				title: finalTitle,
				data:  buildPayload({
					dealId:     deal.id,
					templateId: template.id,
					title:      finalTitle,
					state:      nextState,
				}),
			})
			closeAndReset()
		} finally {
			setIsSaving(false)
		}
	}

	function handleBack() {
		setStepIdx((i) => (i <= 0 ? -1 : i - 1))
	}

	function handleBasicsValid(values: BasicsValues) {
		const next = { ...state, basics: values }
		setState(next)
		advance(next)
	}

	function handleSubjectValid(values: SubjectValues) {
		const next = { ...state, subject: values }
		setState(next)
		advance(next)
	}

	function handleLandlordValid(values: LandlordValues) {
		const next = { ...state, landlord: values }
		setState(next)
		advance(next)
	}

	function handleTenantValid(values: TenantValues) {
		const next = { ...state, tenant: values }
		setState(next)
		advance(next)
	}

	function handleTermsValid(values: TermsValues) {
		const next = { ...state, terms: values }
		setState(next)
		advance(next)
	}

	function handlePaymentsValid(values: PaymentsValues) {
		const next = { ...state, payments: values }
		setState(next)
		advance(next)
	}

	// Utilities live in step-local state and only reach `onValid` on the final
	// save — sync them into wizard state as they change so Back/stepper
	// navigation and the draft-persistence effect don't lose the edits.
	function handleUtilitiesChange(utilities: PaymentsUtilityRow[]) {
		setState((s) => ({ ...s, payments: { ...s.payments, utilities } }))
	}

	const showStepper  = !onGatewayStep
	const nextBtnLabel = isLastStep ? t('deals.documents.wizard.create') : t('deals.documents.wizard.next')

	return (
		<Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : closePreservingDraft())} disablePointerDismissal>
			<DialogContent
				showCloseButton={false}
				className="flex flex-col p-0 gap-0 overflow-hidden rounded-(--radius-lg)"
				style={{ width: 732, maxWidth: '95vw', maxHeight: '90vh' }}
			>
				<PanelCardHeader
					className="p-6"
					title={
						<DialogTitle
							className="text-2xl leading-8 tracking-[-0.6px] font-[500] text-(--color-foreground) whitespace-nowrap"
							render={<span />}
						>
							{title}
						</DialogTitle>
					}
					description={[deal.propertyTitle, deal.propertyAddress].filter(Boolean).join(', ') || undefined}
					onClose={closePreservingDraft}
					stages={showStepper && (
						<PanelFilters
							className="p-0"
							filters={(
								<Stages layout="row">
									{WIZARD_STEP_ORDER.map((key, i) => (
										<DealStageElement
											key={key}
											className="w-[124px]"
											type={i <= stepIdx ? 'selected' : 'default'}
											label={t(`deals.documents.wizard.steps.${key}`)}
											onClick={() => {
												if (i <= stepIdx) setStepIdx(i)
											}}
										/>
									))}
								</Stages>
							)}
						/>
					)}
				/>

				<div className="flex-1 overflow-y-auto px-6 pb-8 pt-2">
					{onGatewayStep ? (
						<StepBasics
							initial={state.basics}
							existingNumbers={existingNumbers}
							formId={STEP_FORM_ID}
							onValid={handleBasicsValid}
						/>
					) : currentWizardStep === 'subject' ? (
						<StepSubject
							initial={state.subject}
							formId={STEP_FORM_ID}
							documentType={state.basics.documentType}
							onValid={handleSubjectValid}
						/>
					) : currentWizardStep === 'landlord' ? (
						<StepLandlord
							initial={state.landlord}
							formId={STEP_FORM_ID}
							onValid={handleLandlordValid}
						/>
					) : currentWizardStep === 'tenant' ? (
						<StepTenant
							initial={state.tenant}
							formId={STEP_FORM_ID}
							documentType={state.basics.documentType}
							onValid={handleTenantValid}
						/>
					) : currentWizardStep === 'terms' ? (
						<StepTerms
							initial={state.terms}
							formId={STEP_FORM_ID}
							onValid={handleTermsValid}
						/>
					) : currentWizardStep === 'payments' ? (
						<StepPayments
							initial={state.payments}
							formId={STEP_FORM_ID}
							onValid={handlePaymentsValid}
							onUtilitiesChange={handleUtilitiesChange}
						/>
					) : null}
				</div>

				<PanelFooter className="gap-2 px-6 pt-0 pb-6" align="end">
					{!onGatewayStep && (
						<Button variant="secondary" size="md" onClick={handleBack}>
							{t('deals.documents.wizard.back')}
						</Button>
					)}
					<Button type="submit" size="md" form={STEP_FORM_ID} disabled={(isLastStep && !template) || isSaving}>
						{isSaving ? t('common.saving') : nextBtnLabel}
					</Button>
				</PanelFooter>

				<span className="sr-only" aria-hidden>{deal.id}</span>
			</DialogContent>
		</Dialog>
	)
}
